/**
 * messagingMessages.ts
 *
 * Single source of truth for all message definitions.
 * Every message: id, tier, placement, priority, emoji, copy, GLP-1 variant.
 *
 * Priority: lower number = higher priority within tier.
 * Copy: headline drives the message. Body supports it (one sentence).
 *
 * Target: src/services/messaging/messagingMessages.ts
 */

import type { MessageDefinition } from './messaging.types';

export const MESSAGE_DEFINITIONS: MessageDefinition[] = [

  // ── T1 — Daily critical nudges (Nutrition only) ───────────────────────────
  // Trigger window: 17:00–21:00. Requires food logged. Max 1/day (general cap).
  // Priority order: undereating > protein > hydration > fibre.

  {
    id: 'undereating',
    tier: 'T1',
    placement: 'nutrition',
    priority: 1,
    emoji: '🔋',
    copy: {
      headline: 'Calories very low — you should eat now',
      body: 'Aim for at least your minimum to support energy and recovery.',
    },
    glp1Copy: {
      headline: 'Calories very low — you should eat now',
      body: 'GLP-1 reduces hunger, but your body still needs fuel.',
    },
  },

  {
    id: 'protein_low',
    tier: 'T1',
    placement: 'nutrition',
    priority: 2,
    emoji: '💪',
    copy: {
      headline: 'Protein low — add protein next meal',
      body: 'Add a serving to help protect muscle.',
    },
    glp1Copy: {
      headline: 'Protein low — add protein next meal',
      body: 'Appetite suppression can make this harder — protecting muscle matters most.',
    },
  },

  {
    id: 'hydration_low',
    tier: 'T1',
    placement: 'nutrition',
    priority: 3,
    emoji: '💧',
    copy: {
      headline: 'Hydration low — drink water now',
      body: 'Aim for your daily target.',
    },
  },

  {
    id: 'fibre_low',
    tier: 'T1',
    placement: 'nutrition',
    priority: 4,
    emoji: '🥦',
    copy: {
      headline: 'Fibre low — add fruit or veg',
      body: 'Add vegetables, fruit, or whole foods to support digestion and health.',
    },
  },

  // ── T2 — Threshold recovery (Nutrition only) ──────────────────────────────
  // Fires only on first threshold crossing per day.
  // Suppressed if T1 has fired today.
  // Max 1/day (general cap).

  {
    id: 'protein_recovered',
    tier: 'T2',
    placement: 'nutrition',
    priority: 1,
    emoji: '💪',
    copy: {
      headline: 'Protein now on track',
      body: 'Keep it there — consistency builds results.',
    },
  },

  {
    id: 'hydration_recovered',
    tier: 'T2',
    placement: 'nutrition',
    priority: 2,
    emoji: '💧',
    copy: {
      headline: 'Hydration now on track',
      body: 'Keep it there.',
    },
  },

  // ── T3 — Weekly patterns (Home only) ─────────────────────────────────────
  // Runs Monday morning. Evaluates completed prior Mon–Sun.
  // Requires ≥ 4 logged days (except strength_missing which uses workout history).
  // Suppressed if T1 fired on Monday.
  // Max 1/week.
  // Priority order: undereating > protein > hydration > fibre > strength > logging.

  {
    id: 'weekly_undereating',
    tier: 'T3',
    placement: 'home',
    priority: 1,
    emoji: '🔋',
    copy: {
      headline: "You're consistently under-eating",
      body: 'This can affect progress — aim for your minimum.',
    },
  },

  {
    id: 'weekly_protein_low',
    tier: 'T3',
    placement: 'home',
    priority: 2,
    emoji: '💪',
    copy: {
      headline: 'Protein has been low this week',
      body: 'Prioritise it daily to support muscle and recovery.',
    },
  },

  {
    id: 'weekly_hydration_low',
    tier: 'T3',
    placement: 'home',
    priority: 3,
    emoji: '💧',
    copy: {
      headline: 'Hydration has been low this week',
      body: 'Aim for your daily minimum consistently.',
    },
  },

  {
    id: 'weekly_fibre_low',
    tier: 'T3',
    placement: 'home',
    priority: 4,
    emoji: '🥦',
    copy: {
      headline: 'Fibre has been low this week',
      body: 'Increase whole foods to support digestion and health.',
    },
  },

  {
    id: 'weekly_strength_missing',
    tier: 'T3',
    placement: 'home',
    priority: 5,
    emoji: '🏋️',
    copy: {
      headline: 'No strength work logged this week',
      body: 'Adding 1–2 sessions can help protect muscle while in a deficit.',
    },
  },

  {
    id: 'weekly_logging_inconsistent',
    tier: 'T3',
    placement: 'home',
    priority: 6,
    emoji: '📝',
    copy: {
      headline: 'Tracking has been inconsistent',
      body: 'Logging daily gives you a clearer picture.',
    },
  },


  // ── T6 — Recovery nudges (Home only) ───────────────────────────────────────
  // Fires when user has missed 1–2 consecutive logging days.
  // Once per gap — does not repeat on day 2 of the same gap.
  // 13 variants across 3 groups. Copy resolved in engine by day-of-week rotation.
  // Only one definition needed — variants live in messagingEngine RECOVERY_VARIANTS.

  {
    id: 'missed_day_recovery',
    tier: 'T6_RECOVERY',
    placement: 'home',
    priority: 1,
    emoji: '🔄',
    copy: {
      // Fallback only — actual copy resolved from RECOVERY_VARIANTS in engine
      headline: 'Missed a day — just log your next meal',
      body: "You're still on track. You're still on track.",
    },
  },

  // ── T5 — GLP-1 meal nudges (Home only) ───────────────────────────────────
  // GLP-1 mode must be enabled. Separate cap (max 1/day).
  // Never shown alongside a general engine message on the same day.
  // T1 takes priority: if T1 fired today, GLP-1 meal nudge is suppressed.

  {
    id: 'glp1_meal_1',
    tier: 'T5_GLP1_MEAL',
    placement: 'home',
    priority: 1,
    emoji: '💉',
    copy: {
      headline: "You haven't logged a meal yet",
      body: 'Even small meals help keep energy, digestion, and muscle steady.',
    },
  },

  {
    id: 'glp1_meal_2',
    tier: 'T5_GLP1_MEAL',
    placement: 'home',
    priority: 2,
    emoji: '💉',
    copy: {
      headline: "You haven't logged enough meals today",
      body: 'Even small meals help maintain energy, digestion, and muscle.',
    },
  },
  // ── T7 — On track signal (Home only) ────────────────────────────────────────
  // Fires when: ≥5 logged days in last 7, protein on target, ≥2 exercise sessions,
  // average deficit between 200–1200 kcal. Once per week.

  {
    id: 'on_track',
    tier: 'T7_ON_TRACK',
    placement: 'home',
    priority: 1,
    emoji: '✅',
    copy: {
      headline: "You're on track — great job!",
      body: 'Logging, protein and exercise are all looking good this week.',
    },
  },

  // ── T9 — Normative reassurance (Home only) ───────────────────────────────────
  // Fires within first 21 days, max once every 3 days.
  // 20 variants across 4 groups. Copy resolved in engine randomly.
  // Tone: calm, factual, anonymous, non-judgmental. No numbers, no performance.

  {
    id: 'normative_reassurance',
    tier: 'T9_NORMATIVE',
    placement: 'home',
    priority: 1,
    emoji: '💬',
    copy: {
      // Fallback only — actual copy resolved from NORMATIVE_VARIANTS in engine
      headline: 'This pattern is normal early on.',
      body: 'Most routines take a little time to settle.',
    },
  },

  // ── T10 — Pattern-detection guidance (Home only) ────────────────────────────
  // Fires when rolling 7-day drop detected in logging, protein, or workouts.
  // Requires 14 days since first log. 7-day cooldown per metric, 3-day global cap.
  // Priority: logging > protein > workout. Only one fires at a time.
  // Tone: calm, specific, recoverable, non-judgmental.

  {
    id: 'pattern_drop_logging',
    tier: 'T10_PATTERN_DROP',
    placement: 'home',
    priority: 1,
    emoji: '📝',
    copy: {
      headline: 'Logging has dipped a little — just start again with your next meal.',
      body: 'A small drop in consistency is normal — one entry gets you moving again.',
    },
  },

  {
    id: 'pattern_drop_protein',
    tier: 'T10_PATTERN_DROP',
    placement: 'home',
    priority: 2,
    emoji: '💪',
    copy: {
      headline: 'Protein has dropped recently — aim to make your next meal protein-first.',
      body: 'A small protein dip is easy to recover from — focus on your next meal.',
    },
  },

  {
    id: 'pattern_drop_workout',
    tier: 'T10_PATTERN_DROP',
    placement: 'home',
    priority: 3,
    emoji: '🏋️',
    copy: {
      headline: 'Workouts have dropped off a little — one session this week gets momentum back.',
      body: 'A short break is normal — one resistance session can restart the rhythm.',
    },
  },

  // ── T11 — A1 first-entry preview (Home only) ───────────────────────────────
  // Fires once ever after first nutrition or workout entry.
  // High priority — sits above reassurance/progress, below urgent safety.
  // Tone: immediate, calm, lightly interpretive, not celebratory.

  {
    id: 'first_entry_nutrition',
    tier: 'T11_FIRST_ENTRY',
    placement: 'home',
    priority: 1,
    emoji: '📊',
    copy: {
      headline: 'Good start — your daily picture is beginning to build.',
      body: 'Keep logging to get the most from CoreTrak.',
    },
  },

  {
    id: 'first_entry_workout',
    tier: 'T11_FIRST_ENTRY',
    placement: 'home',
    priority: 2,
    emoji: '📊',
    copy: {
      headline: 'Great start — your activity is now part of today\'s picture.',
      body: 'Keep logging to get the most from CoreTrak.',
    },
  },

];

/** Look up a message definition by ID. */
export function getMessageById(id: string): MessageDefinition | undefined {
  return MESSAGE_DEFINITIONS.find(m => m.id === id);
}
